import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { User, UserRole, SponsorProfile } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import KalaKrutTokenABI from '@/abis/KalaKrutToken.json';
import { ethers, EventLog } from 'ethers';

interface SponsorRealDataTableProps {
  currentUserRole: UserRole;
  currentUserId: string;
}

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const TableActions: React.FC<{ 
  sponsor: User;
  role: UserRole; 
  onAction: (action: string, sponsorId: string) => void;
}> = ({ sponsor, role, onAction }) => {
  const handleAction = (action: string) => onAction(action, sponsor.id);
  const isAdmin = role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE;
  
  if (isAdmin) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('edit')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">Edit</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }
  return <></>;
};

const SponsorRealDataTable: React.FC<SponsorRealDataTableProps> = ({ currentUserRole, currentUserId }) => {
  const [allSponsors, setAllSponsors] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFallback, setIsFallback] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsFallback(false);
    addLog("Starting sponsor data fetch.", LogLevel.INFO, LogCategory.DATA);

    if (provider && isConnected) {
      try {
        const tokenContract = new ethers.Contract(contractAddresses.KalaKrutToken, KalaKrutTokenABI.abi, provider);
        const transferFilter = tokenContract.filters.Transfer(null, null, null);
        const transferEvents = await tokenContract.queryFilter(transferFilter);

        const sponsorAddresses = [...new Set(transferEvents.map(event => (event as EventLog).args.to))];
        
        const sponsorPromises = sponsorAddresses.map(async (address: string) => {
          const response = await fetch(`${API_BASE_URL}/api/users?walletAddress=${address}&role=SPONSOR`, { credentials: 'include' });
          if (response.ok) return response.json();
          return { id: address, walletAddress: address, name: 'On-Chain Sponsor', role: UserRole.SPONSOR, sponsorProfile: { companyName: 'N/A' } as SponsorProfile };
        });

        const sponsors = (await Promise.all(sponsorPromises)).flat().filter(a => a);
        setAllSponsors(sponsors);
        addLog(`Fetched ${sponsors.length} on-chain sponsors.`, LogLevel.SUCCESS, LogCategory.DATA);
        setIsLoading(false);
        return;
      } catch (e: unknown) {
        const onChainError = "Could not load on-chain sponsor data. Falling back to API.";
        addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
        setError(onChainError);
        notify(onChainError, 'warn');
        setIsFallback(true);
      }
    }

    // Fallback to Off-Chain API
    try {
      let apiData;
      const response = await fetch(`${API_BASE_URL}/api/users?role=SPONSOR`, { credentials: 'include' });

      if (response.ok) {
        apiData = await response.json();
        addLog(`Fetched ${apiData.length} sponsors from REAL API as fallback.`, LogLevel.INFO, LogCategory.API);
      } else {
        addLog(`Real API fallback failed, using mock data.`, LogLevel.WARN, LogCategory.API);
        const mockResponse = await fetch('/sponsors.json');
        if (!mockResponse.ok) throw new Error('Real API and mock data both failed.');
        apiData = await mockResponse.json();
        notify('Displaying mock data. Wallet not connected and API failed.', 'warn');
      }
      
      if (Array.isArray(apiData)) {
        setAllSponsors(apiData);
        setIsFallback(true);
      } else {
        throw new Error('Fetched off-chain data is not an array.');
      }

    } catch (e: unknown) {
      const fallbackError = `Critical: Failed to load any sponsor data: ${(e as Error).message}`;
      addLog(fallbackError, LogLevel.CRITICAL, LogCategory.API);
      setError(fallbackError);
      notify(fallbackError, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (action: string, sponsorId: string) => {
    if (action === 'delete') {
      if (!window.confirm('Are you sure you want to delete this sponsor?')) return;
      addLog(`Admin ${currentUserId} initiated deletion of sponsor ${sponsorId}`, LogLevel.INFO, LogCategory.SPONSOR);
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${sponsorId}`, { method: 'DELETE', credentials: 'include' });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete sponsor via API.');
        }
        setAllSponsors(current => current.filter(a => a.id !== sponsorId));
        notify(`Sponsor ${sponsorId} was successfully deleted.`, 'success');
        addLog(`Sponsor ${sponsorId} successfully deleted by ${currentUserId}.`, LogLevel.INFO, LogCategory.SPONSOR);
      } catch (err) {
        notify((err as Error).message, 'error');
        addLog(`Error deleting sponsor ${sponsorId}: ${(err as Error).message}`, LogLevel.ERROR, LogCategory.SPONSOR);
      }
    } else {
      notify(`Action '${action}' for sponsor '${sponsorId}' is not implemented.`, 'info');
    }
  };

  const filteredSponsors = useMemo(() => {
    return allSponsors.filter(a => 
      (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      ((a.sponsorProfile as SponsorProfile)?.companyName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allSponsors, searchTerm]);

  const displayedSponsors = useMemo(() => {
      return filteredSponsors.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredSponsors, page]);

  const hasMore = page * PAGE_SIZE < filteredSponsors.length;

  if (isLoading && allSponsors.length === 0) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading sponsors...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Sponsors</h2>
        <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
                type="text"
                placeholder="Search by name or company..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
            />
        </div>
      </div>
      {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
      {(!isConnected || isFallback) && (
        <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
          Note: Displaying fallback data. For real-time information, connect your wallet.
        </div>
      )}

      <Table>
        <TableCaption>A list of sponsors in the roster. {allSponsors.length === 0 && !isLoading && "No sponsors found."}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Company</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedSponsors.map((sponsor) => (
            <TableRow key={sponsor.id}>
              <TableCell>{sponsor.name || 'N/A'}</TableCell>
              <TableCell>{(sponsor.sponsorProfile as SponsorProfile)?.companyName || 'N/A'}</TableCell>
              <TableCell className="text-right">
                <TableActions sponsor={sponsor} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

        {displayedSponsors.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No sponsors found for your search term.</p>
            </div>
        )}

      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredSponsors.length / PAGE_SIZE)}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SponsorRealDataTable;
